import { faExclamationTriangle, faPlusSquare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { FC } from 'react'
import { Interval } from 'web/utils/interval-utils'
import { secondsToTimestamp } from 'web/utils/time-utils'
import { onKbClick } from 'web/utils/utils'
import { GameIcon } from '../generic/GameIcon'
import { WithTooltip } from '../generic/WithTooltip'

export type EventAssignmentPayload = {
    spellState: {
        id: string
        name: string
        icon_name: string
        cooldown: number
        isUniqueInSetup: boolean
    }

    assignmentState: {
        uniqueId: string
        isOverlappingSelf: boolean
        tooltipState: {
            eventOffset: number
            prevUseOffset: number | undefined
            nextUseOffset: number | undefined
            prevHeldFor: number | undefined
            nextHeldFor: number | undefined
            prevExtraUseLatest: Interval | undefined
            nextExtraUseLatest: Interval | undefined
        }
    }

    playerState: {
        id: string
        name: string
        classShortName: string
    }

    interactive?: {
        onHoverEnter: () => void
        onHoverLeave: () => void

        onClick?: (saUniqueId: string) => void
    }
}

type EventAssignmentProps = {
    eventAssignmentPayload: EventAssignmentPayload
}

export const EventAssignment: FC<EventAssignmentProps> = (props) => {

    const { eventAssignmentPayload } = props
    const { spellState, assignmentState, playerState } = eventAssignmentPayload

    const { isOverlappingSelf, tooltipState } = assignmentState

    const { interactive } = eventAssignmentPayload
    const isClickable = interactive != null && interactive.onClick != null

    function timeFormatter(value: number | undefined) {
        if (typeof value === "number") {
            return secondsToTimestamp(value)
        }

        return "N/A"
    }

    function heldForTimeFormatter(heldForSeconds: number | undefined) {
        if (heldForSeconds == null) {
            return ''
        }

        return ` | Held for: ${secondsToTimestamp(heldForSeconds)}`
    }

    const formattedTimestamps = {
        prev: timeFormatter(tooltipState.prevUseOffset)
            + heldForTimeFormatter(tooltipState.prevHeldFor),

        next: timeFormatter(tooltipState.nextUseOffset)
            + heldForTimeFormatter(tooltipState.nextHeldFor),

        prevExtra: timeFormatter(tooltipState.prevExtraUseLatest?.from) + " - " +
            timeFormatter(tooltipState.prevExtraUseLatest?.to),

        nextExtra: timeFormatter(tooltipState.nextExtraUseLatest?.from) + " - " +
            timeFormatter(tooltipState.nextExtraUseLatest?.to)
    }

    const messageParts = [
        // TODO: This is a quick addition to help see who a spell is
        // assigned to when leaning on the `@target` naming
        // convention, it's a really janky implementation since it
        // breaks CD warnings too -- Remove this once you somehow
        // figure out how to add `@target` directly to assignments.
        `${playerState.name}`,

        `Cooldown: ${secondsToTimestamp(spellState.cooldown)}`,
        ``,
        `Earliest prev use: ${secondsToTimestamp(Math.max(0, eventAssignmentPayload.assignmentState.tooltipState.eventOffset - spellState.cooldown))}`,
        `Earliest next use: ${secondsToTimestamp(spellState.cooldown + eventAssignmentPayload.assignmentState.tooltipState.eventOffset)}`,
        ``,
        `Prev use: ${formattedTimestamps.prev}`,
        `Next use: ${formattedTimestamps.next}`,
    ]

    const hasNextExtra = tooltipState.nextExtraUseLatest != null

    const hasPrevExtra =
        (tooltipState.prevExtraUseLatest != null) &&
        // TODO: This feels iffy, very quick and dirty
        // -----------------------
        // Hide any extra uses in the first 15 seconds, filters out
        // some noise where the app wants us to use CDs on pull
        (tooltipState.prevExtraUseLatest.to >= 15)

    if (hasPrevExtra || hasNextExtra) {
        messageParts.push(``)
    }

    if (hasPrevExtra) {
        messageParts.push(`Additional usage available between: ${formattedTimestamps.prevExtra}`)
    }

    if (hasNextExtra) {
        messageParts.push(`Additional usage available between: ${formattedTimestamps.nextExtra}`)
    }

    if (isOverlappingSelf) {

        messageParts.push(``)
        messageParts.push(
            `⚠️ Warning: This assignment is overlapping another`,
            `one and will not be off CD when needed.`,
        )

        let linebreakAdded = false

        if (tooltipState.prevUseOffset != null) {
            const timeUntilEvent = tooltipState.eventOffset - tooltipState.prevUseOffset
            const timeMissing = spellState.cooldown - timeUntilEvent

            if (timeMissing > 0) {
                if (!linebreakAdded) {
                    linebreakAdded = true
                    messageParts.push(``)
                }

                messageParts.push(
                    `Overlaps prev by: ${secondsToTimestamp(timeMissing)}`
                )
            }
        }

        if (tooltipState.nextUseOffset != null) {
            const timeUntilEvent = tooltipState.nextUseOffset - tooltipState.eventOffset
            const timeMissing = spellState.cooldown - timeUntilEvent

            if (timeMissing > 0) {
                if (!linebreakAdded) {
                    linebreakAdded = true
                    messageParts.push(``)
                }

                messageParts.push(
                    `Overlaps next by: ${secondsToTimestamp(timeMissing)}`
                )
            }
        }

    }

    const tooltipMessage = messageParts.join("</br>")

    const onClick = () => {
        if (interactive != null && interactive.onClick != null) {
            interactive.onClick(assignmentState.uniqueId)
            interactive.onHoverLeave()
        }
    }

    return (
        <WithTooltip place={"right"} fullWidth>
            {tooltipId => (
                <div
                    data-html
                    data-for={tooltipId}
                    data-tip={tooltipMessage}

                    tabIndex={isClickable ? 0 : undefined}
                    onClick={onClick}
                    onKeyPress={onKbClick(onClick)}
                    onMouseEnter={interactive?.onHoverEnter}
                    onMouseLeave={interactive?.onHoverLeave}

                    className={classNames(
                        'd-flex align-items-stretch',
                        "small text-monospace",
                        `bg-${playerState.classShortName}-darker`,
                        { 'cursor-pointer': isClickable },
                        { 'cursor-default': isClickable === false },
                    )}

                    style={{
                        // NOTICE: Needed to play nice with WrappingDiv
                        ...(props as any).style,

                        textShadow: "0px 0px 5px #00000095",
                        color: "white",
                        border: "1px solid #3d3d3d",
                    }}
                >

                    <GameIcon
                        size="22px"
                        icon_name={spellState.icon_name}
                    />

                    {spellState.isUniqueInSetup === false &&
                        <div
                            className="d-flex align-items-center px-2"
                            style={{ borderLeft: "1px solid #3d3d3d" }}
                        >
                            {playerState.name.substr(0, 3)}
                        </div>
                    }

                    {isOverlappingSelf &&
                        <div
                            className="d-flex align-items-center px-2"
                            style={{ borderLeft: "1px solid #3d3d3d" }}
                        >
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                color={"white"}
                                size="xs"
                            />
                        </div>
                    }

                    {(hasPrevExtra || hasNextExtra) &&
                        <div
                            className="d-flex align-items-center px-2"
                            style={{ borderLeft: "1px solid #3d3d3d" }}
                        >
                            <FontAwesomeIcon
                                icon={faPlusSquare}
                                color={"white"}
                                size="xs"
                            />
                        </div>
                    }

                </div>
            )}
        </WithTooltip>
    )
}
